import type {
  EdtActivity,
  EdtDurationUnit,
  EdtEntry,
  EdtEntryInput,
  HilloNumber,
} from '@iculogbook/shared';
import { EDT_DURATION_UNIT_MINUTES, MAX_EDT_DURATION_MINUTES } from '@iculogbook/shared';

/**
 * The state behind the EDT/Teaching form, and the pure functions that move it
 * around. No React and no Dexie, so the rules that matter can be tested
 * directly — the same arrangement as `procedureForm.ts` and `caseForm.ts`.
 *
 * Much smaller than either, because an EDT entry has no patient. There is no
 * age band, no capability level, and nothing that has to be dropped when
 * another field changes. What is left is the same discipline about optional
 * fields: an untouched box becomes an **absent** field rather than an empty
 * string, because absent means "not recorded" and "" would mean "recorded as
 * nothing", which the PDF would then have to render.
 */

export type EdtFormValues = {
  /** Null until chosen. Required, so the form cannot save without it. */
  activity: EdtActivity | null;
  title: string;
  occurredAt: Date;
  /**
   * Optional end of a period of work. Null means the entry happened at
   * `occurredAt` and nowhere else, which is the ordinary case.
   */
  endsAt: Date | null;
  /** Null until chosen. Optional, so null is a perfectly good final answer. */
  durationMinutes: number | null;
  note: string;
  hillos: readonly HilloNumber[];
};

/**
 * A blank entry. `activity` starts null rather than guessed: the eleven kinds
 * are used in genuinely different proportions by different trainees, a wrong
 * preselection is one that gets saved by accident, and an unset required chip
 * row is the same pattern the procedure form already uses for age band.
 */
export function initialValues(occurredAt: Date): EdtFormValues {
  return {
    activity: null,
    title: '',
    occurredAt,
    endsAt: null,
    durationMinutes: null,
    note: '',
    hillos: [],
  };
}

/**
 * Loads a stored entry, or an unsaved copy of one, back into the form. Absent
 * optional fields become the form's empty values; `buildEdtEntryInput` turns
 * them back into absent fields on the way out.
 */
export function edtValuesFrom(record: EdtEntry | EdtEntryInput): EdtFormValues {
  return {
    activity: record.activity,
    title: record.title,
    occurredAt: record.occurredAt,
    endsAt: record.endsAt ?? null,
    durationMinutes: record.durationMinutes ?? null,
    note: record.note ?? '',
    hillos: record.hillos ?? [],
  };
}

export type MissingField = 'activity' | 'title';

/** The two choices with no safe default. Everything else is optional. */
export function missingRequired(values: EdtFormValues): MissingField[] {
  const missing: MissingField[] = [];
  if (values.activity === null) missing.push('activity');
  if (values.title.trim() === '') missing.push('title');
  return missing;
}

/**
 * A typed amount and its unit, as whole minutes inside the allowed range, or
 * null if it is not a usable duration.
 *
 * Fractions are accepted for hours and days — "1.5 days" and "2.5 hours" are
 * how people say it — and rejected for minutes, where a fraction of a minute
 * is not a thing anybody means. Whatever the unit, the *result* has to land on
 * a whole minute, because that is what the column stores: 2.5 hours is 150 and
 * fine, 1.0001 days is not.
 */
export function parseDuration(value: string, unit: EdtDurationUnit = 'minutes'): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;

  const amount = Number(trimmed);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (unit === 'minutes' && !Number.isInteger(amount)) return null;

  const minutes = amount * EDT_DURATION_UNIT_MINUTES[unit];

  if (!Number.isInteger(minutes) || minutes < 1 || minutes > MAX_EDT_DURATION_MINUTES) {
    return null;
  }

  return minutes;
}

/**
 * Turns the form state into the record the repository writes, or null if a
 * required choice is still missing.
 *
 * The result is still handed to `edtEntryInputSchema` before anything is
 * written. This function decides what to include; the shared schema decides
 * whether it is valid, and it is the only thing that does.
 */
export function buildEdtEntryInput(values: EdtFormValues): EdtEntryInput | null {
  const title = values.title.trim();
  if (values.activity === null || title === '') return null;

  const note = values.note.trim();

  return {
    activity: values.activity,
    title,
    occurredAt: values.occurredAt,
    ...(values.endsAt === null ? {} : { endsAt: values.endsAt }),
    ...(values.durationMinutes === null ? {} : { durationMinutes: values.durationMinutes }),
    ...(note === '' ? {} : { note }),
    ...(values.hillos.length === 0 ? {} : { hillos: [...values.hillos] }),
  };
}
